import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { RoutePath } from '@shared/route-path.enum';
import { SfInputComponent } from '@shared/sf-form-controls/sf-input/sf-input/sf-input.component';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, finalize, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

enum LoginError {
	EmailNotVerified,
	StudentAccountInactive,
	Default,
}

@Component({
	selector: 'sf-login',
	templateUrl: './login.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnDestroy, AfterViewInit {
	@ViewChild('loginInput') loginInput: SfInputComponent;

	public loginForm = this.fb.group({
		email: ['', Validators.required],
		password: ['', Validators.required],
	});
	public RoutePath: typeof RoutePath = RoutePath;
	public loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public verifyEmail: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public errorMessage: BehaviorSubject<string> = new BehaviorSubject<string>(null);

	private readonly destroy: Subject<void> = new Subject();

	public get emailControl(): FormControl {
		return this.loginForm.controls.email as FormControl;
	}

	constructor(
		private fb: FormBuilder,
		private authService: AuthService
	) {}

	public ngAfterViewInit(): void {
		this.loginInput.focus();
	}

	public ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}

	public loginOnEnter(e: KeyboardEvent): void {
		if (e.code === '13') {
			this.onSubmit();
		}
	}

	public onSubmit(): void {
		this.loading.next(true);
		this.errorMessage.next(null);
		this.authService
			.login(this.loginForm.getRawValue())
			.pipe(
				catchError((error) => this.handleLoginError(error)),
				tap(() => this.authService.navigateAfterLogin()),
				take(1),
				finalize(() => this.loading.next(false)),
				takeUntil(this.destroy)
			)
			.subscribe();
	}

	public backToLogin(): void {
		this.verifyEmail.next(false);
	}

	private resolveLoginErrorType(message: string): LoginError {
		if (message === 'Your student account is inactive!') {
			return LoginError.StudentAccountInactive;
		}
		if (message === 'Your email address is not verified.') {
			return LoginError.EmailNotVerified;
		}
		return LoginError.Default;
	}

	private resolveLoginErrorTranslationKey(type: LoginError): string {
		const prefix = 'sf.module.auth.log_in.error.';
		if (type === LoginError.StudentAccountInactive) {
			return `${prefix}student_account_inactive`;
		}
		return `${prefix}failed`;
	}

	private handleLoginError(error: HttpErrorResponse): Observable<never> {
		const errorType = this.resolveLoginErrorType(error.error && error.error.message);
		if (errorType === LoginError.EmailNotVerified) {
			this.verifyEmail.next(true);
		} else {
			this.errorMessage.next(this.resolveLoginErrorTranslationKey(errorType));
			this.loginInput.focus();
		}
		return EMPTY;
	}
}
